import {
  LayoutDashboard,
  Home,
  Phone,
  ShoppingBag,
  Settings,
  SlidersHorizontal,
  Tag,
  Users,
  Shield,
  Package,
  Truck,
  Wallet,
  Palette,
  Ruler,
  Layers,
} from "lucide-react";
import { MENU, PERMISSION } from "../../lib/permissions";

const VIEW = [PERMISSION.VIEW];
const CREATE = [PERMISSION.CREATE];

/**
 * Menu definition consumed by <Sidebar />.
 * - `menu_id` maps to the backend permission record.
 * - `require` lists the action labels needed on that menu_id for the entry
 *   to show. A super-admin bypasses all checks.
 * - `superAdminOnly` marks entries that only super-admins may see (e.g.
 *   admin management), independent of any menu_id permission.
 */
export const MENU_SECTIONS = [
  {
    label: "Main",
    items: [
      {
        title: "Dashboard",
        icon: LayoutDashboard,
        menu_id: MENU.DASHBOARD,
        items: [
          { path: "/dashboard", label: "Dashboard", require: VIEW },
        ],
      },
    ],
  },
  {
    label: "Commerce",
    items: [
      {
        title: "Catalogue",
        icon: Package,
        menu_id: MENU.CATALOGUE,
        items: [
          {
            label: "Product",
            icon: Package,
            children: [
              { path: "/addproduct", label: "Add Product", require: CREATE },
              { path: "/product", label: "Product List", require: VIEW },
              { path: "/bulk/edit", label: "Bulk Edit", require: VIEW },
            ],
          },
          { path: "/catalog", label: "Categories", icon: Layers, require: VIEW },
          {
            label: "Brand",
            icon: Tag,
            children: [
              { path: "/addbrand", label: "Add Brand", require: CREATE },
              { path: "/brand", label: "Brand List", require: VIEW },
            ],
          },
          {
            label: "Attributes",
            icon: SlidersHorizontal,
            children: [
              { path: "/addsize", label: "Add Size", require: CREATE },
              { path: "/size", label: "Size List", require: VIEW },
              { path: "/addcolor", label: "Add Color", require: CREATE },
              { path: "/color", label: "Color List", require: VIEW },
            ],
          },
        ],
      },
      {
        title: "Orders",
        icon: ShoppingBag,
        menu_id: MENU.ORDER,
        items: [
          { path: "/order/list", label: "Order List", require: VIEW },
        ],
      },
      {
        title: "Offers",
        icon: Tag,
        menu_id: MENU.OFFERS,
        items: [
          { path: "/addoffers", label: "Add Offer", require: CREATE },
          { path: "/offerlist", label: "Offer List", require: VIEW },
        ],
      },
      {
        title: "Wallet",
        icon: Wallet,
        menu_id: MENU.WALLET,
        items: [
          { path: "/wallet/add", label: "Add Wallet", require: CREATE },
          { path: "/wallet/list", label: "Wallet List", require: VIEW },
          {
            label: "Wallet Validities",
            icon: Wallet,
            children: [
              { path: "/wallet/validities/add", label: "Add Wallet Validities", require: CREATE },
              { path: "/wallet/validities/list", label: "Wallet Validities List", require: VIEW },
            ],
          },
          { path: "/loyalty-points", label: "Loyalty Points", require: VIEW },
        ],
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        title: "Driver",
        icon: Truck,
        menu_id: MENU.DRIVER,
        items: [
          { path: "/driver/add", label: "Add Driver", require: CREATE },
          { path: "/driverlist", label: "Driver List", require: VIEW },
        ],
      },
    ],
  },
  {
    label: "Management",
    items: [
      {
        title: "Customer",
        icon: Users,
        menu_id: MENU.USER,
        items: [
          {
            label: "Customer",
            children: [
              { path: "/user/add", label: "Add Customer", require: CREATE },
              { path: "/user", label: "Customer List", require: VIEW },
            ],
          },
          { path: "/cartlist", label: "Cart", require: VIEW },
          { path: "/rating", label: "Rating", require: VIEW },
          { path: "/wishlist", label: "Wishlist", require: VIEW },
          { path: "/searches", label: "Searches", require: VIEW },
        ],
      },
      {
        title: "Admins",
        icon: Shield,
        menu_id: MENU.USER,
        superAdminOnly: true,
        items: [
          { path: "/admin/add", label: "Add Admin", require: CREATE },
          { path: "/admin", label: "Admin List", require: VIEW },
        ],
      },
      {
        title: "Contacts",
        icon: Phone,
        menu_id: MENU.DASHBOARD,
        showCount: true,
        items: [
          { path: "/contactlist", label: "Contact List", require: VIEW },
        ],
      },
    ],
  },
  {
    label: "Content",
    items: [
      {
        title: "Home Page",
        icon: Home,
        menu_id: MENU.HOME,
        items: [
          { path: "/banners", label: "Banners", require: VIEW },
          { path: "/dynamic-sections", label: "Sections", require: VIEW },
          { path: "/promo-popups", label: "Popups", require: VIEW },
          { path: "/trust-strip", label: "Trust Strip", require: VIEW },
        ],
      },
      {
        title: "Settings",
        icon: Settings,
        menu_id: MENU.SETTINGS,
        items: [
          { path: "/contactdetails", label: "Contact Details", require: VIEW },
          { path: "/content-pages", label: "Pages", require: VIEW },
          { path: "/content-pages/faq", label: "FAQ", require: VIEW },
          { path: "/emailevents", label: "Email Delivery", require: VIEW },
          { path: "/notification", label: "Notification", require: VIEW },
          { path: "/notification/history", label: "Notification history", require: VIEW },
        ],
      },
    ],
  },
];

export const labelForPath = (pathname) => {
  for (const section of MENU_SECTIONS) {
    for (const menu of section.items) {
      for (const item of menu.items) {
        if (item.path && pathname.startsWith(item.path)) {
          return { label: item.label, toplabel: item.label };
        }
        if (item.children) {
          const child = item.children.find(
            (c) => c.path && pathname.startsWith(c.path),
          );
          if (child) {
            return { label: child.label, toplabel: child.label };
          }
        }
      }
    }
  }
  return null;
};
